import { Confirm } from '@/components/main/Confirm'
import CopytoClipboard from '@/components/main/Copytoclipboard'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toastHandler } from '@/components/utils/Toast'
import ShareFile from '@/modules/files/components/ShareFile'
import { useDeleteDocument } from '@/services/folders/mutations'
import { keyDocument, keyDocuments } from '@/services/folders/queries'
import { Copy, Edit, Eye, MoreHorizontal, Share, Trash } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import ManageMediaFolders from './ManageMediaFolders'

type Props = {
    item: any
    setShowImageViewer: (show: boolean) => void
}

export const MediaItemActions = ({ item, setShowImageViewer }: Props) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [showActions, setShowActions] = useState(false)

    const { mutate: deleteMediaAsset, isLoading } = useDeleteDocument({
        refetch: [keyDocument(`id=${item?.parent?.id}`), keyDocuments(``)],
        queryParams: {
            id: item?.id as string,
        },
        onSuccess: () => {
            toastHandler({ message: 'Media Folder deleted' })

            const search = location.search || ''
            if (item?.parent) {
                navigate(`/dashboard/media/${item.parent.id}${search}`)
            } else {
                navigate(`/dashboard/media${search}`)
            }
        },
        onError: (error: any) => {
            const status = error?.response?.status
            const message = error?.response?.data?.error
            if (status === 409 && message) {
                toast.error(message)
            } else {
                toast.error('Failed to delete. Please try again.')
            }
        },
    })

    const isPdf = item.url?.includes('.pdf') ?? false
    const isTxt = item.url?.includes('.txt') ?? false

    const handleViewClick = () => {
        if (isPdf || isTxt) {
            const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(item.url)}&embedded=true`
            window.open(viewerUrl, '_blank')
        } else {
            setShowImageViewer(true)
        }
        setShowActions(false)
    }

    const handleConfirmDelete = () => {
        deleteMediaAsset({})
    }

    return (
        <Popover open={showActions} onOpenChange={setShowActions}>
            <PopoverTrigger className='min-w-max max-w-max' onClick={e => e.stopPropagation()}>
                <div className='flex items-center p-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm hover:bg-white hover:shadow-md transition-all duration-200'>
                    <MoreHorizontal className='w-4 h-4 text-gray-600' />
                </div>
            </PopoverTrigger>
            <PopoverContent className='w-48 p-2 shadow-lg border border-gray-200' onClick={e => e.stopPropagation()}>
                <div className='space-y-1'>
                    <div
                        onClick={handleViewClick}
                        className='flex items-center gap-3 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150'
                    >
                        <Eye className='w-4 h-4' />
                        View
                    </div>

                    <ManageMediaFolders
                        trigger={
                            <div className='flex items-center gap-3 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 w-full'>
                                <Edit className='w-4 h-4' />
                                Edit
                            </div>
                        }
                        onClose={() => setShowActions(false)}
                        folder={item}
                        refetchKey={[]}
                    />

                    <CopytoClipboard
                        content={item.url}
                        trigger={
                            <div
                                onClick={() => setShowActions(false)}
                                className='flex items-center gap-3 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150'
                            >
                                <Copy className='w-4 h-4' />
                                Copy URL
                            </div>
                        }
                    />

                    <ShareFile
                        trigger={
                            <div className='flex items-center gap-3 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150'>
                                <Share className='w-4 h-4' />
                                Share
                            </div>
                        }
                        document={item}
                        insideDropdown={true}
                    />

                    <div className='border-t border-gray-100 my-1' />

                    <Confirm
                        trigger={
                            <div
                                className='flex items-center gap-3 px-3 py-2 text-sm text-red-600 rounded-md cursor-pointer hover:bg-red-50 hover:text-red-700 transition-colors duration-150'
                                onClick={e => e.stopPropagation()}
                            >
                                <Trash className='w-4 h-4' />
                                Delete
                            </div>
                        }
                        onClose={() => setShowActions(false)}
                        title='Delete Media File'
                        message='Are you sure you want to delete this media file? This action cannot be undone.'
                        primaryAction={handleConfirmDelete}
                        loading={isLoading}
                    />
                </div>
            </PopoverContent>
        </Popover>
    )
}
